'use client';

import { refreshSession } from '@odys/auth/client';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

import { sessionExpiryFromCookies } from './session-expiry';

/** Marge avant l'échéance : la rotation part pendant que le token est encore valide. */
const ROTATION_MARGIN_MS = 60_000;

/** Keycloak injoignable ≠ session morte : on retente au lieu de déconnecter. */
const TRANSIENT_RETRY_MS = 30_000;

/**
 * Garde-fou anti-boucle : si la rotation réussit sans que le cookie lisible bouge (cookie
 * écrit sur un domaine qu'on ne relit pas, horloge décalée), la prochaine échéance calculée
 * reste dans le passé. Sans ce plancher, on rafalerait des rotations en continu.
 */
const MIN_ROTATION_INTERVAL_MS = 5_000;

/**
 * Plancher de la rotation suivante, une fois celle-ci réussie.
 *
 * `sessionExpiryFromCookies` élit le premier badge encore valide, et sa précédence place le
 * cookie agence devant le cookie partagé. Mais c'est le **serveur** qui choisit la jambe qu'il
 * fait tourner : un `catalogToken` valide dont le gate Mirage ne rend aucun verdict laisse
 * `/api/auth/refresh` relayer la session DMC (cf. `carriesAgencyVerdict`). L'échéance élue n'a
 * alors pas bougé, et `MIN_ROTATION_INTERVAL_MS` seul en ferait la cadence — une douzaine de
 * grants de trop, sur un refresh token à usage unique, pendant toute la marge restante.
 *
 * On attend donc que ce badge périme : `sessionExpiryFromCookies` élira ensuite celui qui a
 * réellement tourné, et la cadence repart sur son échéance.
 */
function delayAfterRotation(rotatedFor: number): number {
    if (sessionExpiryFromCookies(document.cookie) !== rotatedFor) return MIN_ROTATION_INTERVAL_MS;

    // Badge déjà périmé et pourtant toujours élu : aucun cookie lisible n'a bougé (cookie écrit
    // sur un domaine qu'on ne relit pas, horloge décalée). On retombe sur le plancher, sinon on
    // rafalerait à la milliseconde.
    return Math.max(rotatedFor - Date.now() + 1, MIN_ROTATION_INTERVAL_MS);
}

/**
 * Rotation **proactive** de la session, côté navigateur.
 *
 * `useAuthedFetch` ne couvre que les surfaces qui appellent l'API : après 30 minutes
 * (`accessTokenLifespan`) passées à naviguer entre pages RSC, ou dans un onglet laissé ouvert,
 * l'access token expire et la personne retombe en Visitor alors que son refresh token vit 7
 * jours. Ce hook programme la rotation sur l'échéance réelle du token, lue dans le cookie
 * non-httpOnly, et la rejoue à chaque retour au premier plan — les timers d'un onglet caché
 * sont throttlés par le navigateur, on ne peut pas s'y fier.
 *
 * Monté une fois pour toute l'app (cf. `SessionRotation`). Les appels concurrents éventuels
 * sont de toute façon coalescés par {@link refreshSession}.
 */
export function useSessionRotation(): void {
    const router = useRouter();

    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const plan = (minDelayMs = 0) => {
            // Un seul minuteur en vie : `rotate` replanifie en fin de rotation, et un retour au
            // premier plan pendant celle-ci a pu en armer un autre entre-temps. Sans cette
            // annulation, le handle du premier était simplement perdu et la cadence doublait.
            clearTimeout(timer);

            const expiresAt = sessionExpiryFromCookies(document.cookie);
            // Pas de session lisible : rien à faire tourner. Un visiteur reste visiteur.
            if (expiresAt === null) return;

            const delay = Math.max(expiresAt - Date.now() - ROTATION_MARGIN_MS, minDelayMs);
            // L'échéance visée part avec le minuteur : c'est elle que `delayAfterRotation`
            // compare au cookie relu, pour savoir si la rotation a porté sur ce badge-là.
            timer = setTimeout(() => void rotate(expiresAt), delay);
        };

        /**
         * Ne reprend le minuteur que si un badge encore valide subsiste. Une échéance déjà
         * passée signifie que le 401 n'a rien effacé de lisible : reprogrammer dessus
         * rejouerait la même rotation refusée toutes les `MIN_ROTATION_INTERVAL_MS`, sans fin.
         */
        const planIfStillLive = () => {
            const expiresAt = sessionExpiryFromCookies(document.cookie);
            if (expiresAt !== null && expiresAt > Date.now()) plan();
        };

        const rotate = async (rotatedFor: number) => {
            const outcome = await refreshSession();
            if (cancelled) return;

            if (outcome === 'transient') return plan(TRANSIENT_RETRY_MS);
            // Session définitivement morte : le Server Component racine relit la session et
            // rend l'état déconnecté, plutôt que de laisser une UI qui ment.
            //
            // Mais le 401 ne condamne qu'UNE des deux sessions : `/api/auth/refresh` prend la
            // jambe agence en premier et efface ses cookies au passage. On reprend donc le
            // minuteur sur ce qui subsiste — une session DMC encore vivante, typiquement —
            // sinon elle mourrait à son échéance sans jamais avoir été tournée.
            if (outcome === 'expired') {
                router.refresh();
                return planIfStillLive();
            }

            plan(delayAfterRotation(rotatedFor));
        };

        // Pas de `clearTimeout` ici : `plan` en porte désormais l'invariant, et le dupliquer
        // laisserait croire que ce call-site est le seul à pouvoir doubler le minuteur.
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') plan();
        };

        plan();
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [router]);
}
